import {
  createDocumentExportFileName,
  normalizeDocumentExportContent,
  PDF_MIME_TYPE,
  splitDocumentExportParagraphs,
  type DocumentExporter,
  type DocumentExportInput,
} from '../documentExport';

import {
  millimetersToPoints,
} from '../documentBranding';

type PdfMakeDocumentInstance = {
  getBlob: () => Promise<Blob>;
};

type PdfMakeRuntime = {
  addVirtualFileSystem:
    (
      virtualFileSystem:
        unknown
    ) => void;

  createPdf:
    (
      definition:
        Record<string, unknown>
    ) => PdfMakeDocumentInstance;
};

let runtimePromise:
  Promise<PdfMakeRuntime> |
  null =
    null;

const unwrapModule = (
  value: unknown
): unknown => {
  if (
    value &&
    typeof value === 'object' &&
    'default' in value
  ) {
    return (
      value as {
        default: unknown;
      }
    ).default;
  }

  return value;
};

const loadPdfMake =
  async () => {
    if (!runtimePromise) {
      runtimePromise =
        (async () => {
          const [
            pdfMakeModule,
            pdfFontsModule,
          ] =
            await Promise.all([
              import(
                'pdfmake/build/pdfmake'
              ),

              import(
                'pdfmake/build/vfs_fonts'
              ),
            ]);

          const pdfMake =
            unwrapModule(
              pdfMakeModule
            ) as PdfMakeRuntime;

          const virtualFileSystem =
            unwrapModule(
              pdfFontsModule
            );

          if (
            !pdfMake ||
            typeof pdfMake.createPdf !==
              'function' ||
            typeof pdfMake.addVirtualFileSystem !==
              'function'
          ) {
            throw new Error(
              'Motor de PDF indisponível.'
            );
          }

          pdfMake
            .addVirtualFileSystem(
              virtualFileSystem
            );

          return pdfMake;
        })();
    }

    return runtimePromise;
  };

const buildPdfContent = (
  value: string
) => {
  const paragraphs =
    splitDocumentExportParagraphs(
      value
    );

  return paragraphs.map(
    (paragraph) => {
      if (!paragraph) {
        return {
          text: ' ',

          margin: [
            0,
            0,
            0,
            9,
          ],
        };
      }

      return {
        text:
          paragraph,

        alignment:
          'justify',

        margin: [
          0,
          0,
          0,
          9,
        ],
      };
    }
  );
};

const buildHeader = (
  input:
    DocumentExportInput,
  leftMargin: number,
  rightMargin: number
) => {
  const branding =
    input.branding;

  if (
    !branding ||
    (
      !(
        branding.showLogo &&
        branding.logoDataUrl
      ) &&
      !(
        branding.showHeader &&
        branding.headerText
      )
    )
  ) {
    return undefined;
  }

  const stack:
    Array<Record<string, unknown>> =
      [];

  if (
    branding.showLogo &&
    branding.logoDataUrl
  ) {
    stack.push({
      image:
        branding.logoDataUrl,

      width:
        millimetersToPoints(
          branding.logoWidthMm
        ),

      alignment:
        'center',

      margin: [
        0,
        6,
        0,
        3,
      ],
    });
  }

  if (
    branding.showHeader &&
    branding.headerText
  ) {
    stack.push({
      text:
        branding.headerText,

      alignment:
        'center',

      fontSize:
        8.5,

      lineHeight:
        1.15,

      bold:
        false,

      margin: [
        0,
        0,
        0,
        3,
      ],
    });
  }

  stack.push({
    canvas: [
      {
        type:
          'line',

        x1:
          0,

        y1:
          0,

        x2:
          515 -
          leftMargin -
          rightMargin,

        y2:
          0,

        lineWidth:
          0.5,

        lineColor:
          '#D1D5DB',
      },
    ],

    margin: [
      0,
      2,
      0,
      0,
    ],
  });

  return {
    stack,

    margin: [
      leftMargin,
      0,
      rightMargin,
      0,
    ],
  };
};

const buildFooter = (
  input:
    DocumentExportInput,
  currentPage: number,
  pageCount: number,
  leftMargin: number,
  rightMargin: number
) => {
  const branding =
    input.branding;

  if (
    !branding ||
    (
      !(
        branding.showFooter &&
        branding.footerText
      ) &&
      !branding.showPageNumbers
    )
  ) {
    return undefined;
  }

  const stack:
    Array<Record<string, unknown>> =
      [
        {
          canvas: [
            {
              type:
                'line',

              x1:
                0,

              y1:
                0,

              x2:
                515 -
                leftMargin -
                rightMargin,

              y2:
                0,

              lineWidth:
                0.5,

              lineColor:
                '#D1D5DB',
            },
          ],

          margin: [
            0,
            0,
            0,
            3,
          ],
        },
      ];

  if (
    branding.showFooter &&
    branding.footerText
  ) {
    stack.push({
      text:
        branding.footerText,

      alignment:
        'center',

      fontSize:
        7.5,

      lineHeight:
        1.1,

      margin: [
        0,
        0,
        0,
        branding.showPageNumbers
          ? 2
          : 0,
      ],
    });
  }

  if (
    branding.showPageNumbers
  ) {
    stack.push({
      text:
        `Página ${currentPage} de ${pageCount}`,

      alignment:
        'center',

      fontSize:
        7.5,
    });
  }

  return {
    stack,

    margin: [
      leftMargin,
      0,
      rightMargin,
      4,
    ],
  };
};

const createPdfArtifact =
  async (
    input:
      DocumentExportInput
  ) => {
    const content =
      normalizeDocumentExportContent(
        input.content
      );

    if (!content.trim()) {
      throw new Error(
        'O documento não possui conteúdo para exportação.'
      );
    }

    const pdfMake =
      await loadPdfMake();

    const title =
      input.title.trim() ||
      'Documento';

    const creator =
      input.creator?.trim() ||
      'BM Legal OS';

    const subject =
      input.subject?.trim() ||
      'Documento jurídico';

    const branding =
      input.branding;

    const leftMargin =
      millimetersToPoints(
        branding?.marginLeftMm ??
        30
      );

    const topMargin =
      millimetersToPoints(
        branding?.marginTopMm ??
        25
      );

    const rightMargin =
      millimetersToPoints(
        branding?.marginRightMm ??
        20
      );

    const bottomMargin =
      millimetersToPoints(
        branding?.marginBottomMm ??
        20
      );

    const definition:
      Record<string, unknown> = {
      pageSize:
        'A4',

      pageMargins: [
        leftMargin,
        topMargin,
        rightMargin,
        bottomMargin,
      ],

      info: {
        title,

        author:
          creator,

        subject,

        creator:
          'BM Legal OS',

        producer:
          'BM Legal OS',
      },

      defaultStyle: {
        font:
          'Roboto',

        fontSize:
          12,

        lineHeight:
          1.5,

        color:
          '#111827',
      },

      header:
        branding
          ? () =>
              buildHeader(
                input,
                leftMargin,
                rightMargin
              )
          : undefined,

      footer:
        branding
          ? (
              currentPage:
                number,
              pageCount:
                number
            ) =>
              buildFooter(
                input,
                currentPage,
                pageCount,
                leftMargin,
                rightMargin
              )
          : undefined,

      content:
        buildPdfContent(
          content
        ),
    };

    const generatedBlob =
      await pdfMake
        .createPdf(
          definition
        )
        .getBlob();

    const blob =
      generatedBlob.type ===
      PDF_MIME_TYPE
        ? generatedBlob
        : new Blob(
            [
              generatedBlob,
            ],
            {
              type:
                PDF_MIME_TYPE,
            }
          );

    return {
      format:
        'pdf' as const,

      fileName:
        createDocumentExportFileName(
          title,
          'pdf'
        ),

      mimeType:
        PDF_MIME_TYPE,

      blob,
    };
  };

export const pdfDocumentExporter:
  DocumentExporter = {
  format: 'pdf',

  export:
    createPdfArtifact,
};